import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../theme/useTheme';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  TextInput,
  ScrollView,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import DLOModal from '../DLOModal';
import DLODropdown from '../DLODropdown';
import DLOButton from '../DLOButton';
import { DLODropdownOption } from '../../types/dlo.types';

// Add Zone Icon
const AddZoneIcon = ({ color = '#FFFFFF' }) => (
  <Svg width="20" height="20" viewBox="0 0 21 20" fill="none">
    <Path
      d="M16.0217 10.8334H11.0217V15.8334H9.355V10.8334H4.355V9.16675H9.355V4.16675H11.0217V9.16675H16.0217V10.8334Z"
      fill={color}
    />
  </Svg>
);

// Action Icons from Figma
const SaveIcon = ({ color = '#3A3A3A' }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M14.1667 2.5H4.16667C3.24167 2.5 2.5 3.25 2.5 4.16667V15.8333C2.5 16.75 3.24167 17.5 4.16667 17.5H15.8333C16.75 17.5 17.5 16.75 17.5 15.8333V5.83333L14.1667 2.5ZM15.8333 15.8333H4.16667V4.16667H13.475L15.8333 6.525V15.8333ZM10 10C8.61667 10 7.5 11.1167 7.5 12.5C7.5 13.8833 8.61667 15 10 15C11.3833 15 12.5 13.8833 12.5 12.5C12.5 11.1167 11.3833 10 10 10ZM5 5H12.5V8.33333H5V5Z"
      fill={color}
    />
  </Svg>
);

const ClearIcon = ({ color = '#3A3A3A' }) => (
 <svg xmlns="http://www.w3.org/2000/svg" width="17" height="15" viewBox="0 0 17 15" fill="none">
  <path d="M12.6773 12.5H16.6356V14.1667H11.0106L12.6773 12.5ZM2.26064 14.1667L0.489805 12.3958C0.170361 12.0764 0.00716608 11.6806 0.000221631 11.2083C-0.00672281 10.7361 0.149527 10.3333 0.468972 10L9.63564 0.5C9.95508 0.166667 10.3474 0 10.8127 0C11.278 0 11.6704 0.159722 11.9898 0.479167L16.1356 4.625C16.4551 4.94444 16.6148 5.34028 16.6148 5.8125C16.6148 6.28472 16.4551 6.68056 16.1356 7L9.13564 14.1667H2.26064ZM8.42731 12.5L14.969 5.79167L10.844 1.66667L1.63564 11.1667L2.96897 12.5H8.42731Z" fill={color}/>
</svg>
);

const DeleteIcon = ({ color = '#3A3A3A' }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M13.3333 7.5V15.8333H6.66663V7.5H13.3333ZM12.0833 2.5H7.91663L7.08329 3.33333H4.16663V5H15.8333V3.33333H12.9166L12.0833 2.5ZM15 5.83333H4.99996V15.8333C4.99996 16.75 5.74996 17.5 6.66663 17.5H13.3333C14.25 17.5 15 16.75 15 15.8333V5.83333Z"
      fill={color}
    />
  </Svg>
);

interface VolumeDistributionRow {
  id: string;
  zone: string;
  volumeDistribution: {
    sun: string;
    mon: string;
    tue: string;
    wed: string;
    thu: string;
    fri: string;
    sat: string;
  };
  kcph: {
    override1: string;
    override2: string;
  };
  weeklyDsdVolume: {
    default: string;
    target: string;
  };
}

export interface DLOVolumeDistributionTableProps {
  data?: VolumeDistributionRow[];
  onSave?: () => void;
  onAddZone?: () => void;
  onZoneAdded?: (zone: string) => void;
  onRowSave?: (rowId: string) => void;
  onRowClear?: (rowId: string) => void;
  onRowDelete?: (rowId: string) => void;
  weeklyPercentage?: string;
  onWeeklyPercentageChange?: (value: string) => void;
  headerTitle?: string;
  style?: any;
}

const DLOVolumeDistributionTable: React.FC<DLOVolumeDistributionTableProps> = ({
  data = [],
  onSave,
  onAddZone,
  onZoneAdded,
  onRowSave,
  onRowClear,
  onRowDelete,
  weeklyPercentage = '80%',
  onWeeklyPercentageChange,
  headerTitle = 'NON-DSD ZONE VOLUME DISTRIBUTION BY DAY',
  style
}) => {
  const isDarkTheme = useTheme().resolvedTheme === 'dark';
  const [volumeData, setVolumeData] = useState<VolumeDistributionRow[]>(
    data.length > 0 ? data : [
      {
        id: '1',
        zone: 'BAY',
        volumeDistribution: {
          sun: '0',
          mon: '0',
          tue: '0',
          wed: '0',
          thu: '0',
          fri: '0',
          sat: '0',
        },
        kcph: {
          override1: '',
          override2: '',
        },
        weeklyDsdVolume: {
          default: '',
          target: '',
        },
      },
      {
        id: '2',
        zone: 'BULK',
        volumeDistribution: {
          sun: '0',
          mon: '0',
          tue: '0',
          wed: '0',
          thu: '0',
          fri: '0',
          sat: '0',
        },
        kcph: {
          override1: '',
          override2: '',
        },
        weeklyDsdVolume: {
          default: '',
          target: '',
        },
      },
    ]
  );

  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [isAddZoneModalVisible, setIsAddZoneModalVisible] = useState(false);
  const [selectedZone, setSelectedZone] = useState<DLODropdownOption | null>(null);
  const [percentage, setPercentage] = useState(weeklyPercentage);

  // Update volumeData when data prop changes
  useEffect(() => {
    if (data && data.length > 0) {
      console.log('DLOVolumeDistributionTable: Updating with new data:', data);
      setVolumeData(data);
    }
  }, [data]);

  // Generate zone options dynamically based on current table data
  const zoneOptions: DLODropdownOption[] = React.useMemo(() => {
    if (volumeData && volumeData.length > 0) {
      // Extract unique zones from current table data
      const uniqueZones = [...new Set(volumeData.map(row => row.zone))];
      
      console.log('Generating zone options from table data:', uniqueZones);
      
      return uniqueZones.map(zoneName => ({
        id: zoneName.toLowerCase(),
        label: zoneName.charAt(0) + zoneName.slice(1).toLowerCase(), // Proper case
        value: zoneName
      }));
    }
    
    // Fallback to default zones if no data
    return [
      { id: 'bay', label: 'Bay', value: 'BAY' },
      { id: 'bulk', label: 'Bulk', value: 'BULK' },
      { id: 'receiving', label: 'Receiving', value: 'RECEIVING' },
      { id: 'shipping', label: 'Shipping', value: 'SHIPPING' },
      { id: 'storage', label: 'Storage', value: 'STORAGE' },
    ];
  }, [volumeData]);

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const updateVolumeDistribution = (rowId: string, day: string, value: string) => {
    setVolumeData(prev => prev.map(row => 
      row.id === rowId 
        ? { ...row, volumeDistribution: { ...row.volumeDistribution, [day]: value } }
        : row
    ));
  };

  const updateKcph = (rowId: string, field: 'override1' | 'override2', value: string) => {
    setVolumeData(prev => prev.map(row => 
      row.id === rowId 
        ? { ...row, kcph: { ...row.kcph, [field]: value } }
        : row
    ));
  };

  const updateWeeklyDsdVolume = (rowId: string, field: 'default' | 'target', value: string) => {
    setVolumeData(prev => prev.map(row => 
      row.id === rowId 
        ? { ...row, weeklyDsdVolume: { ...row.weeklyDsdVolume, [field]: value } }
        : row
    ));
  };

  const handleAddZone = () => {
    setIsAddZoneModalVisible(true);
  };

  const handleZoneModalSave = () => {
    if (selectedZone) {
      const newId = (volumeData.length + 1).toString();
      
      const newRow: VolumeDistributionRow = {
        id: newId,
        zone: selectedZone.value,
        volumeDistribution: {
          sun: '0',
          mon: '0',
          tue: '0',
          wed: '0',
          thu: '0',
          fri: '0',
          sat: '0',
        },
        kcph: {
          override1: '',
          override2: '',
        },
        weeklyDsdVolume: {
          default: '',
          target: '',
        },
      };

      setVolumeData(prev => [...prev, newRow]);
      setIsAddZoneModalVisible(false);
      setSelectedZone(null);
      
      onAddZone?.();
      onZoneAdded?.(selectedZone.value);
    }
  };

  const handleZoneModalCancel = () => {
    setIsAddZoneModalVisible(false);
    setSelectedZone(null);
  };

  const handlePercentageChange = (value: string) => {
    setPercentage(value);
    onWeeklyPercentageChange?.(value);
  };

  const isSaveDisabled = () => {
    return false;
  };

  return (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={Platform.OS === 'web'}
      contentContainerStyle={styles.scrollContainer}
    >
      <View style={[styles.container, style]}>
        {/* Main Header */}
        <View style={styles.mainHeader}>
          <View style={styles.addZoneButton}>
            <TouchableOpacity 
              style={styles.addZoneButtonInner}
              onPress={handleAddZone}
              activeOpacity={0.7}
            >
              <AddZoneIcon />
              <Text style={styles.addZoneText}>Add Zone</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.headerDivider} />

          <View style={styles.volumeDistributionSection}>
            <Text style={styles.headerSectionText}>{headerTitle}</Text>
          </View>

          <View style={styles.headerDividerAfterVolumeSection} />

          <View style={styles.kcphSection}>
            <Text style={styles.headerSectionText}>KCPH</Text>
          </View>

          <View style={styles.headerDivider} />

          <View style={styles.weeklyDsdSection}>
            <Text style={styles.headerSectionText}>WEEKLY DSD VOLUME</Text>
          </View>

          <View style={styles.headerDivider} />

          <View style={styles.actionSection}>
            <Text style={styles.headerSectionText}>ACTION</Text>
          </View>
        </View>

        {/* Sub Header */}
        <View style={styles.subHeader}>
          <View style={styles.zoneSubColumn}>
            <Text style={styles.subHeaderText}>Zone</Text>
          </View>

          {/* Volume Distribution Days */}
          <View style={styles.volumeDistributionDaysGroup}>
            {dayLabels.map((day) => (
              <React.Fragment key={`volume-${day}`}>
                <View style={styles.dayColumnDivider} />
                <View style={styles.daySubColumn}>
                  <Text style={styles.subHeaderText}>{day}</Text>
                </View>
              </React.Fragment>
            ))}
          </View>

          {/* KCPH - Two Override Columns */}
          <View style={styles.kcphGroup}>
            <View style={styles.dayColumnDivider} />
            <View style={styles.overrideSubColumn}>
              <Text style={styles.subHeaderText}>Override</Text>
            </View>
            <View style={styles.dayColumnDivider} />
            <View style={styles.overrideSubColumn}>
              <Text style={styles.subHeaderText}>Override</Text>
            </View>
          </View>

          {/* Weekly DSD Volume - Default and Target */}
          <View style={styles.weeklyDsdGroup}>
            <View style={styles.dayColumnDivider} />
            <View style={styles.defaultSubColumn}>
              <Text style={styles.subHeaderText}>Default</Text>
            </View>
            <View style={styles.dayColumnDivider} />
            <View style={styles.targetSubColumn}>
              <Text style={styles.subHeaderText}>Target</Text>
            </View>
            <View style={styles.dayColumnDivider} />
          </View>

          <View style={styles.actionSubColumn}>
            <Text style={styles.subHeaderText}> </Text>
          </View>
        </View>

        {/* Data Rows */}
        {volumeData.map((row, rowIndex) => (
          <View key={row.id} style={[
            styles.dataRow, 
            rowIndex === volumeData.length - 1 && styles.lastDataRow
          ]}>
            <View style={styles.zoneSubColumn}>
              <Text style={styles.zoneText}>{row.zone}</Text>
            </View>
            
            {/* Volume Distribution Inputs */}
            <View style={styles.volumeDistributionDaysGroup}>
              {dayLabels.map((day) => (
                <React.Fragment key={`volume-input-${day}`}>
                  <View style={styles.inputColumnDivider} />
                  <View style={styles.daySubColumn}>
                    <View style={styles.inputContainer}>
                      <TextInput
                        style={[styles.input, styles.volumeInput]}
                        value={row.volumeDistribution[day.toLowerCase() as keyof typeof row.volumeDistribution]}
                        onChangeText={(value) => updateVolumeDistribution(row.id, day.toLowerCase(), value)}
                        placeholder="0"
                        placeholderTextColor="#3A3A3A"
                        maxLength={10}
                      />
                    </View>
                  </View>
                </React.Fragment>
              ))}
            </View>

            {/* KCPH - Two Override Inputs */}
            <View style={styles.kcphGroup}>
              <View style={styles.inputColumnDivider} />
              <View style={styles.overrideSubColumn}>
                <View style={styles.inputContainer}>
                  <TextInput
                    style={[styles.input, styles.overrideInput]}
                    value={row.kcph.override1}
                    onChangeText={(value) => updateKcph(row.id, 'override1', value)}
                    placeholder=""
                    placeholderTextColor="#3A3A3A"
                    maxLength={10}
                  />
                </View>
              </View>
              <View style={styles.inputColumnDivider} />
              <View style={styles.overrideSubColumn}>
                <View style={styles.inputContainer}>
                  <TextInput
                    style={[styles.input, styles.overrideInput]}
                    value={row.kcph.override2}
                    onChangeText={(value) => updateKcph(row.id, 'override2', value)}
                    placeholder=""
                    placeholderTextColor="#3A3A3A"
                    maxLength={10}
                  />
                </View>
              </View>
            </View>

            {/* Weekly DSD Volume - Default and Target Inputs */}
            <View style={styles.weeklyDsdGroup}>
              <View style={styles.inputColumnDivider} />
              <View style={styles.defaultSubColumn}>
                <View style={styles.inputContainer}>
                  <TextInput
                    style={[styles.input, styles.defaultInput]}
                    value={row.weeklyDsdVolume.default}
                    onChangeText={(value) => updateWeeklyDsdVolume(row.id, 'default', value)}
                    placeholder=""
                    placeholderTextColor="#3A3A3A"
                    maxLength={10}
                  />
                </View>
              </View>
              <View style={styles.inputColumnDivider} />
              <View style={styles.targetSubColumn}>
                <View style={styles.inputContainer}>
                  <TextInput
                    style={[styles.input, styles.targetInput]}
                    value={row.weeklyDsdVolume.target}
                    onChangeText={(value) => updateWeeklyDsdVolume(row.id, 'target', value)}
                    placeholder=""
                    placeholderTextColor="#3A3A3A"
                    maxLength={10}
                  />
                </View>
              </View>
              <View style={styles.inputColumnDivider} />
            </View>

            <View style={styles.actionSubColumn}>
              <View style={styles.actionButtonsContainer}>
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => onRowSave?.(row.id)}
                  onPressIn={() => setHoveredButton(`${row.id}-save`)}
                  onPressOut={() => setHoveredButton(null)}
                  {...Platform.select({
                    web: {
                      onMouseEnter: () => setHoveredButton(`${row.id}-save`),
                      onMouseLeave: () => setHoveredButton(null),
                    },
                  })}
                  activeOpacity={0.7}
                >
                  <SaveIcon color={hoveredButton === `${row.id}-save` ? 'rgb(0, 92, 188)' : '#3A3A3A'} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => onRowClear?.(row.id)}
                  onPressIn={() => setHoveredButton(`${row.id}-clear`)}
                  onPressOut={() => setHoveredButton(null)}
                  {...Platform.select({
                    web: {
                      onMouseEnter: () => setHoveredButton(`${row.id}-clear`),
                      onMouseLeave: () => setHoveredButton(null),
                    },
                  })}
                  activeOpacity={0.7}
                >
                  <ClearIcon color={hoveredButton === `${row.id}-clear` ? 'rgb(0, 92, 188)' : '#3A3A3A'} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => onRowDelete?.(row.id)}
                  onPressIn={() => setHoveredButton(`${row.id}-delete`)}
                  onPressOut={() => setHoveredButton(null)}
                  {...Platform.select({
                    web: {
                      onMouseEnter: () => setHoveredButton(`${row.id}-delete`),
                      onMouseLeave: () => setHoveredButton(null),
                    },
                  })}
                  activeOpacity={0.7}
                >
                  <DeleteIcon color={hoveredButton === `${row.id}-delete` ? 'rgb(0, 92, 188)' : '#3A3A3A'} />
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ))}

        {/* Bottom Row with Percentage and Save Button */}
        <View style={styles.bottomRow}>
          <View style={styles.percentageSection}>
            <TextInput
              style={styles.percentageInput}
              value={percentage}
              onChangeText={handlePercentageChange}
              placeholder="80%"
              placeholderTextColor="#3A3A3A"
              maxLength={5}
            />
          </View>
          <View style={styles.saveButtonSection}>
            <TouchableOpacity
              style={[
                styles.saveButton,
                isSaveDisabled() && styles.saveButtonDisabled,
                isDarkTheme && { backgroundColor: isSaveDisabled() ? '#3A3A3A' : '#3D8BFF' },
              ]}
              onPress={onSave}
              disabled={isSaveDisabled()}
              activeOpacity={0.7}
            >
              <Text style={[
                styles.saveButtonText,
                isSaveDisabled() && styles.saveButtonTextDisabled,
                isDarkTheme && { color: isSaveDisabled() ? '#8C8C8C' : '#FFFFFF' },
              ]}>
                Save
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Add Zone Modal */}
      <DLOModal
        visible={isAddZoneModalVisible}
        onClose={handleZoneModalCancel}
        title="Edit Zone"
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onPrimaryPress={handleZoneModalSave}
        onSecondaryPress={handleZoneModalCancel}
        primaryButtonDisabled={!selectedZone}
        width={400}
      >
        <View style={styles.modalContent}>
          <DLODropdown
            label="Zone"
            placeholder=""
            value={selectedZone}
            options={zoneOptions}
            onSelect={(option) => setSelectedZone(option as DLODropdownOption)}
            size="medium"
            fullWidth
          />
        </View>
      </DLOModal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
    minWidth: '100%',
  },
  container: {
    borderRadius: 8,
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    overflow: 'hidden',
    width: '100%',
    minWidth: Platform.select({
      web: 1000,
      default: 800,
    }),
    maxWidth: '100%',
  },
  
  // Main Header
  mainHeader: {
    backgroundColor: '#005CBC',
    flexDirection: 'row',
    alignItems: 'center',
    height: Platform.select({
      web: 48,
      default: 56,
    }),
  },
  addZoneButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 0.15,
    minWidth: Platform.select({
      web: 120,
      default: 100,
    }),
  },
  addZoneButtonInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  addZoneText: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: Platform.select({
      web: 14,
      default: 13,
    }),
    fontWeight: '700',
    lineHeight: Platform.select({
      web: 20,
      default: 18,
    }),
  },
  headerDivider: {
    width: 1,
    height: 50,
    backgroundColor: '#FFFFFF',
  },
  headerDividerAfterVolumeSection: {
    width: 1,
    height: 50,
    backgroundColor: '#FFFFFF',
  },
  volumeDistributionSection: {
    flex: 1,
    minWidth: Platform.select({
      web: 400,
      default: 350,
    }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  kcphSection: {
    flex: 0.25,
    minWidth: Platform.select({
      web: 160,
      default: 140,
    }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  weeklyDsdSection: {
    flex: 0.25,
    minWidth: Platform.select({
      web: 160,
      default: 140,
    }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionSection: {
    flex: 0.15,
    minWidth: Platform.select({
      web: 120,
      default: 100,
    }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerSectionText: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: Platform.select({
      web: 12,
      default: 11,
    }),
    fontWeight: '700',
    lineHeight: Platform.select({
      web: 18,
      default: 16,
    }),
    textTransform: 'uppercase',
    textAlign: 'center',
  },

  // Sub Header
  subHeader: {
    backgroundColor: '#EFF5FC',
    flexDirection: 'row',
    alignItems: 'center',
    height: Platform.select({
      web: 40,
      default: 48,
    }),
    borderBottomColor: '#E0E0E0',
  },
  zoneSubColumn: {
    flex: 0.15,
    minWidth: Platform.select({
      web: 120,
      default: 100,
    }),
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  volumeDistributionDaysGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  kcphGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 0.25,
    minWidth: Platform.select({
      web: 160,
      default: 140,
    }),
    justifyContent: 'center',
  },
  weeklyDsdGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 0.25,
    minWidth: Platform.select({
      web: 160,
      default: 140,
    }),
    justifyContent: 'center',
  },
  daySubColumn: {
    flex: 1,
    minWidth: Platform.select({
      web: 60,
      default: 50,
    }),
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  overrideSubColumn: {
    flex: 1,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  defaultSubColumn: {
    flex: 1,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  targetSubColumn: {
    flex: 1,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  dayColumnDivider: {
    width: 1,
    height: 38,
    backgroundColor: '#E0E0E0',
    alignSelf: 'stretch',
  },
  actionSubColumn: {
    flex: 0.15,
    minWidth: Platform.select({
      web: 120,
      default: 100,
    }),
    alignItems: 'center',
    justifyContent: 'center',
    height: Platform.select({
      web: 40,
      default: 48,
    }),
  },
  subHeaderText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: Platform.select({
      web: 10,
      default: 12,
    }),
    fontWeight: '500',
    lineHeight: Platform.select({
      web: 16,
      default: 18,
    }),
    textAlign: 'center',
  },

  // Data Rows
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: Platform.select({
      web: 48,
      default: 56,
    }),
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    backgroundColor: '#FFFFFF',
  },
  lastDataRow: {
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  zoneText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
    textAlign: 'center',
  },
  inputColumnDivider: {
    width: 1,
    height: 56,
    backgroundColor: '#E0E0E0',
    alignSelf: 'center',
  },
  inputContainer: {
    flex: 1,
    minWidth: Platform.select({
      web: 55,
      default: 45,
    }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    width: '100%',
    minHeight: Platform.select({
      web: 28,
      default: 32,
    }),
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    paddingHorizontal: Platform.select({
      web: 8,
      default: 12,
    }),
    paddingVertical: Platform.select({
      web: 4,
      default: 6,
    }),
    fontSize: Platform.select({
      web: 12,
      default: 14,
    }),
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '400',
    lineHeight: Platform.select({
      web: 18,
      default: 20,
    }),
    textAlign: 'center',
    color: '#3A3A3A',
  },
  volumeInput: {
    backgroundColor: '#FBFCFE',
  },
  overrideInput: {
    backgroundColor: '#FBFCFE',
  },
  defaultInput: {
    backgroundColor: '#FBFCFE',
  },
  targetInput: {
    backgroundColor: '#FBFCFE',
  },

  // Bottom Row
  bottomRow: {
    flexDirection: 'row',
    height: 56,
    paddingHorizontal: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    backgroundColor: '#FBFCFE',
  },
  percentageSection: {
    flex: 1,
    alignItems: 'flex-start',
    paddingLeft: 12,
  },
  percentageInput: {
    width: 80,
    height: 32,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    paddingHorizontal: 12,
    paddingVertical: 6,
    fontSize: 14,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '400',
    lineHeight: 20,
    textAlign: 'center',
    color: '#3A3A3A',
    backgroundColor: '#F5F5F5',
  },
  saveButtonSection: {
    alignItems: 'flex-end',
  },
  saveButton: {
    width: 90,
    height: 32,
    borderRadius: 24,
    backgroundColor: '#D0D0D0',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  saveButtonDisabled: {
    backgroundColor: '#D0D0D0',
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  saveButtonTextDisabled: {
    color: '#A0A0A0',
  },

  // Action Buttons for each row
  actionButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    gap: Platform.select({
      web: 4,
      default: 6,
    }),
  },
  actionButton: {
    width: Platform.select({
      web: 24,
      default: 28,
    }),
    height: Platform.select({
      web: 24,
      default: 28,
    }),
    borderRadius: Platform.select({
      web: 12,
      default: 14,
    }),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },

  // Modal styles
  modalContent: {
    paddingVertical: 16,
  },
  modalLabel: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
    marginBottom: 8,
  },
});

export default DLOVolumeDistributionTable;
